import type {PayloadAction} from '@reduxjs/toolkit';
import {createSlice} from '@reduxjs/toolkit';
import type {ItemModifyingState, ItemModifyingAction, TimelineItemType} from './timeline.types';

export const TIMELINE_MIN_WIDTH_PER_SECOND = 5;
export const TIMELINE_MAX_WIDTH_PER_SECOND = 500;

interface SubtitlePushedPosition {
  startTime: number;
  endTime: number;
}

export interface TimelineTooltipState {
  left: number;
  top: number;
  text: string;
}

interface TimelineState {
  widthPerSecond: number;
  timelineHeight: number;
  isPanning: boolean;
  /** ID of the currently hovered timeline item */
  hoveredItemId?: string;
  /** Current item being modified (dragged or resized). Undefined when no modification in progress. */
  itemModifying?: ItemModifyingState;
  timelineHoverPosition?: number;
  minWidthAccordingToPosterDuration: number;
  /** Temporary positions for subtitles being pushed during a resize operation */
  pushedSubtitlePositions: Record<string, SubtitlePushedPosition>;
  /** Whether the seekbar is currently being dragged */
  isSeekbarDragging: boolean;
  /** Pixel positions of active snap guide lines (shown during drag/resize) */
  snapGuideLines: number[];
  /** Whether the timeline panel is currently focused (last clicked area was the timeline) */
  isFocused: boolean;
  /** Tooltip shown during resize operations */
  tooltip?: TimelineTooltipState;
}

const initialState: TimelineState = {
  widthPerSecond: 47,
  timelineHeight: 0,
  isPanning: false,
  hoveredItemId: undefined,
  itemModifying: undefined,
  timelineHoverPosition: undefined,
  minWidthAccordingToPosterDuration: TIMELINE_MIN_WIDTH_PER_SECOND,
  pushedSubtitlePositions: {},
  isSeekbarDragging: false,
  snapGuideLines: [],
  isFocused: false,
  tooltip: undefined,
};

/** Payload for starting an item modification (drag or resize) */
interface StartItemModifyingPayload {
  itemId: string;
  action: ItemModifyingAction;
  startTime: number;
  endTime: number;
  timelineItemType: TimelineItemType;
  originalSubRow?: number;
}

/** Payload for updating an item's position/times during modification */
interface UpdateItemModifyingPayload {
  startTime: number;
  endTime: number;
  targetSubRow?: number;
  insertionIndex?: number;
  dragDeltaY?: number;
}

export const timelineSlice = createSlice({
  name: 'timeline',
  initialState,
  reducers: {
    updateTimelineWidthPerSecond: (state, action: PayloadAction<number>): void => {
      const minWidth = Math.max(TIMELINE_MIN_WIDTH_PER_SECOND, state.minWidthAccordingToPosterDuration);
      if (action.payload < minWidth) {
        state.widthPerSecond = minWidth;
      } else if (action.payload > TIMELINE_MAX_WIDTH_PER_SECOND) {
        state.widthPerSecond = TIMELINE_MAX_WIDTH_PER_SECOND;
      } else {
        state.widthPerSecond = action.payload;
      }
    },
    updatePanningStatus: (state, action: PayloadAction<boolean>): void => {
      state.isPanning = action.payload;
    },
    setHoveredItemId: (state, action: PayloadAction<string | undefined>): void => {
      state.hoveredItemId = action.payload;
    },
    /**
     * Start modifying an item (drag or resize).
     * Sets up the itemModifying state with initial values.
     */
    startItemModifying: (state, action: PayloadAction<StartItemModifyingPayload>): void => {
      state.itemModifying = {
        itemId: action.payload.itemId,
        action: action.payload.action,
        startTime: action.payload.startTime,
        endTime: action.payload.endTime,
        timelineItemType: action.payload.timelineItemType,
        originalSubRow: action.payload.originalSubRow,
      };
    },
    /**
     * Update the current modification in progress.
     * Called during pointermove to update visual position.
     */
    updateItemModifying: (state, action: PayloadAction<UpdateItemModifyingPayload>): void => {
      if (state.itemModifying) {
        state.itemModifying.startTime = action.payload.startTime;
        state.itemModifying.endTime = action.payload.endTime;
        state.itemModifying.targetSubRow = action.payload.targetSubRow;
        state.itemModifying.insertionIndex = action.payload.insertionIndex;
        state.itemModifying.dragDeltaY = action.payload.dragDeltaY;
      }
    },
    /**
     * End the current modification.
     * Clears the itemModifying state and any pushed subtitle positions.
     */
    endItemModifying: (state): void => {
      state.itemModifying = undefined;
      state.pushedSubtitlePositions = {};
      state.snapGuideLines = [];
      state.tooltip = undefined;
    },
    setSnapGuideLines: (state, action: PayloadAction<number[]>): void => {
      // Dispatched on every pointermove; keep the old reference when content is unchanged so subscribers don't re-render per move
      const isUnchanged = state.snapGuideLines.length === action.payload.length && action.payload.every((line, index) => line === state.snapGuideLines[index]);
      if (!isUnchanged) {
        state.snapGuideLines = action.payload;
      }
    },
    /**
     * Set temporary pushed positions for subtitles being displaced during a resize.
     * Replaces any previously set pushed positions.
     */
    setPushedSubtitlePositions: (state, action: PayloadAction<Record<string, SubtitlePushedPosition>>): void => {
      state.pushedSubtitlePositions = action.payload;
    },
    updateTimelineHoverPosition: (state, action: PayloadAction<number | undefined>): void => {
      state.timelineHoverPosition = action.payload;
    },
    updatePosterMinWidthAccordingToDuration: (state, action: PayloadAction<number>): void => {
      state.minWidthAccordingToPosterDuration = action.payload;
    },
    updateTimelineHeight: (state, action: PayloadAction<number>): void => {
      state.timelineHeight = action.payload;
    },
    setSeekbarDragging: (state, action: PayloadAction<boolean>): void => {
      state.isSeekbarDragging = action.payload;
    },
    setTimelineFocused: (state, action: PayloadAction<boolean>): void => {
      state.isFocused = action.payload;
    },
    setTimelineTooltip: (state, action: PayloadAction<TimelineTooltipState | undefined>): void => {
      state.tooltip = action.payload;
    },
  },
});

export const {
  updateTimelineWidthPerSecond,
  updateTimelineHeight,
  updatePanningStatus,
  setHoveredItemId,
  startItemModifying,
  updateItemModifying,
  endItemModifying,
  updateTimelineHoverPosition,
  updatePosterMinWidthAccordingToDuration,
  setPushedSubtitlePositions,
  setSeekbarDragging,
  setSnapGuideLines,
  setTimelineFocused,
  setTimelineTooltip,
} = timelineSlice.actions;

export const timelineReducer = timelineSlice.reducer;
